import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Location } from '@angular/common';
import { Store } from '@ngxs/store';
import { Subject, take, takeUntil } from 'rxjs';

import { AuthState } from '@hiptraveler/data-access/auth';
import { filterQueryStringKey, getWindowRef } from '@hiptraveler/common';
import { RegistrationFormStateService } from '../common';

@Injectable()
export class PromoCodeService implements OnDestroy {

  subscription$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private store: Store,
    private stateService: RegistrationFormStateService
  ) { }

  ngOnDestroy(): void {
    this.subscription$.next();
    this.clearUtmQueryParamsWithLocation();
  }

  /**
   * Subscribes to authentication status and applies query param values to the form
   * when the user becomes authenticated.
   */
  subscribeToAuthAndApplyPromoCode(): void {
    this.store.select(AuthState.authenticated).pipe(
      takeUntil(this.subscription$)
    ).subscribe(() => {
      this.applyPromoCodeFromQueryParam();
    });
  }

  /**
   * Reads the 'utm_campaign' query parameter and patches it to the promo code field of the form.
   */
  applyPromoCodeFromQueryParam(): void {
    this.route.queryParamMap.pipe(
      take(1)
    ).subscribe((queryParamMap: ParamMap) => {
      const utmCampaign = queryParamMap.get('utm_campaign');
      this.stateService.form.patchValue({
        promoCode: utmCampaign
      });
    });
  }

  /**
   * Removes all query parameters from the current URL that start with 'utm_'.
   * Preserves all other query parameters and does not trigger a page reload or navigation.
   */

  clearUtmQueryParamsWithLocation(): void {
    const currentParams = { ...this.route.snapshot.queryParams };
  
    const filteredParams = Object.keys(currentParams)
      .filter(key => !key.startsWith('utm_'))
      .reduce((acc, key) => ({ ...acc, [key]: currentParams[key] }), {});
  
    const path = this.location.path().split('?')[0];
  
    const newQueryString = Object.keys(filteredParams).length
      ? '?' + new URLSearchParams(filteredParams as any).toString()
      : '';
  
    this.location.replaceState(path + newQueryString);
    getWindowRef()[filterQueryStringKey] = newQueryString;
  }

}
